"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.POIListType = exports.stopRailgunEngine = exports.startRailgunEngineForPOINode = exports.startRailgunEngine = exports.setOnMerkletreeScanCallback = exports.viewOnlyWalletForID = exports.fullWalletForID = exports.walletForID = exports.getEngine = void 0;
const engine_1 = require("@railgun-community/engine");
Object.defineProperty(exports, "POIListType", { enumerable: true, get: function () { return engine_1.POIListType; } });
const shared_models_1 = require("@railgun-community/shared-models");
const logger_1 = require("../../../utils/logger");
const artifacts_1 = require("./artifacts");
const error_1 = require("../../../utils/error");
const quick_sync_events_graph_1 = require("../quick-sync/quick-sync-events-graph");
const railgun_tx_sync_graph_1 = require("../railgun-txids/railgun-tx-sync-graph");
const wallet_poi_requester_1 = require("../../poi/wallet-poi-requester");
const wallet_poi_1 = require("../../poi/wallet-poi");
const temp_test_wallet_poi_requester_1 = require("../../poi/temp-test-wallet-poi-requester");
let engine;
const getEngine = () => {
    if (!engine) {
        throw new Error('RAILGUN Engine not yet initialized.');
    }
    return engine;
};
exports.getEngine = getEngine;
const walletForID = (id) => {
    const engine = (0, exports.getEngine)();
    const wallet = engine.wallets[id];
    if (!(0, shared_models_1.isDefined)(wallet)) {
        throw new Error('No RAILGUN wallet for ID');
    }
    return wallet;
};
exports.walletForID = walletForID;
const fullWalletForID = (id) => {
    const wallet = (0, exports.walletForID)(id);
    if (!(wallet instanceof engine_1.RailgunWallet)) {
        throw new Error('Can not load View-Only wallet.');
    }
    return wallet;
};
exports.fullWalletForID = fullWalletForID;
const viewOnlyWalletForID = (id) => {
    const wallet = (0, exports.walletForID)(id);
    if (!(wallet instanceof engine_1.ViewOnlyWallet)) {
        throw new Error('Can only load View-Only wallet.');
    }
    return wallet;
};
exports.viewOnlyWalletForID = viewOnlyWalletForID;
const createEngineDebugger = () => {
    return {
        log: (msg) => (0, logger_1.sendMessage)(msg),
        error: (error) => (0, logger_1.sendErrorMessage)(error),
    };
};
const setOnMerkletreeScanCallback = (onMerkletreeScanCallback) => {
    const engine = (0, exports.getEngine)();
    engine.on(engine_1.EngineEvent.MerkletreeHistoryScanStarted, ({ chain }) => onMerkletreeScanCallback({
        scanStatus: shared_models_1.MerkletreeScanStatus.Started,
        chain,
        progress: 0.0,
    }));
    engine.on(engine_1.EngineEvent.MerkletreeHistoryScanUpdate, ({ chain, progress }) => onMerkletreeScanCallback({
        scanStatus: shared_models_1.MerkletreeScanStatus.Updated,
        chain,
        progress,
    }));
    engine.on(engine_1.EngineEvent.MerkletreeHistoryScanComplete, ({ chain }) => onMerkletreeScanCallback({
        scanStatus: shared_models_1.MerkletreeScanStatus.Complete,
        chain,
        progress: 1.0,
    }));
    engine.on(engine_1.EngineEvent.MerkletreeHistoryScanIncomplete, ({ chain }) => onMerkletreeScanCallback({
        scanStatus: shared_models_1.MerkletreeScanStatus.Incomplete,
        chain,
        progress: 1.0,
    }));
};
exports.setOnMerkletreeScanCallback = setOnMerkletreeScanCallback;
const getPOITxidMerklerootValidator = (poiNodeURL) => {
    const poiRequester = (0, shared_models_1.isDefined)(poiNodeURL)
        ? new wallet_poi_requester_1.WalletPOIRequester(poiNodeURL)
        : // TODO: Remove this temp tester
            new temp_test_wallet_poi_requester_1.TempTestWalletPOIRequester();
    const txidMerklerootValidator = (chain, tree, index, merkleroot) => poiRequester.validateRailgunTxidMerkleroot(chain, tree, index, merkleroot);
    return txidMerklerootValidator;
};
const getPOILatestValidatedRailgunTxid = (poiNodeURL) => {
    const poiRequester = (0, shared_models_1.isDefined)(poiNodeURL)
        ? new wallet_poi_requester_1.WalletPOIRequester(poiNodeURL)
        : // TODO: Remove this temp tester
            new temp_test_wallet_poi_requester_1.TempTestWalletPOIRequester();
    const getLatestValidatedRailgunTxid = chain => poiRequester.getLatestValidatedRailgunTxid(chain);
    return getLatestValidatedRailgunTxid;
};
/**
 *
 * @param walletSource - Name for your wallet implementation. Encrypted and viewable in private transaction history. Maximum of 16 characters, lowercase.
 * @param db - LevelDOWN compatible database for storing encrypted wallets.
 * @param shouldDebug - Whether to forward Engine debug logs to Logger.
 * @param artifactStore - Persistent store for downloading large artifact files. See Wallet SDK Developer Guide for platform implementations.
 * @param useNativeArtifacts - Whether to download native C++ or web-assembly artifacts. TRUE for mobile. FALSE for nodejs and browser.
 * @param skipMerkletreeScans - Whether to skip merkletree syncs and private balance scans. Only set to TRUE in shield-only applications that don't load private wallets or balances.
 * @param poiNodeURL - POI aggregator node URL.
 * @param customPOILists - POI lists to use for additional wallet protections after default lists.
 * @returns
 */
const startRailgunEngine = (walletSource, db, shouldDebug, artifactStore, useNativeArtifacts, skipMerkletreeScans, poiNodeURL, customPOILists) => {
    if (engine) {
        return;
    }
    try {
        (0, artifacts_1.setArtifactStore)(artifactStore);
        (0, artifacts_1.setUseNativeArtifacts)(useNativeArtifacts);
        engine = engine_1.RailgunEngine.initForWallet(walletSource, db, artifacts_1.artifactGetterDownloadJustInTime, quick_sync_events_graph_1.quickSyncEventsGraph, railgun_tx_sync_graph_1.quickSyncRailgunTransactions, getPOITxidMerklerootValidator(poiNodeURL), getPOILatestValidatedRailgunTxid(poiNodeURL), shouldDebug ? createEngineDebugger() : undefined, skipMerkletreeScans);
        wallet_poi_1.WalletPOI.init(poiNodeURL, customPOILists ?? [], engine);
    }
    catch (err) {
        throw (0, error_1.reportAndSanitizeError)(exports.startRailgunEngine.name, err);
    }
};
exports.startRailgunEngine = startRailgunEngine;
const startRailgunEngineForPOINode = (db, shouldDebug, artifactStore) => {
    if (engine) {
        return;
    }
    try {
        (0, artifacts_1.setArtifactStore)(artifactStore);
        (0, artifacts_1.setUseNativeArtifacts)(false);
        engine = engine_1.RailgunEngine.initForPOINode(db, artifacts_1.artifactGetterDownloadJustInTime, quick_sync_events_graph_1.quickSyncEventsGraph, railgun_tx_sync_graph_1.quickSyncRailgunTransactions, shouldDebug ? createEngineDebugger() : undefined);
    }
    catch (err) {
        throw (0, error_1.reportAndSanitizeError)(exports.startRailgunEngineForPOINode.name, err);
    }
};
exports.startRailgunEngineForPOINode = startRailgunEngineForPOINode;
const stopRailgunEngine = async () => {
    await engine?.unload();
    engine = undefined;
};
exports.stopRailgunEngine = stopRailgunEngine;
